/**
 * Alertes de la paroisse (manuel 2, Action 07, p. 17) : « Le tableau de bord
 * du curé ouvre sur les alertes, classées par urgence. Une alerte rouge
 * demande une décision dans la journée. Touchez une alerte pour aller à
 * l'écran concerné. »
 *
 * Calcul PUR (testable sans réseau) à partir des lectures déjà faites par les
 * écrans concernés. Une alerte n'existe que si une source réelle la porte :
 * aucune alerte d'exemple, aucune règle sans donnée derrière.
 */
import type { ClotureSemaine } from "@/hooks/useFinance";
import type { MassIntention } from "@/hooks/useIntentions";
import type { SacrementRequest } from "@/hooks/useSacrementRequests";
import type { VisitePastorale } from "@/hooks/useVisitesPastorales";
import { toIsoDay } from "./periodes";
import { montant } from "@/lib/nombres";
import { enAttenteDuCure } from "@/lib/a-valider";

/** jour = rouge (décision dans la journée) ; semaine = orange ; info = bleu. */
export type Urgence = "jour" | "semaine" | "info";

export interface Alerte {
  /** Identifiant stable (data-testid `alerte-<code>`). */
  code: string;
  urgence: Urgence;
  /** Clé i18n sous `dashboard.alertes.items.<cle>` et ses paramètres. */
  cle: string;
  params: Record<string, string | number>;
  /** Écran ouvert au clic, et sa page (droits d'accès). */
  route: string;
  pageId: number;
}

/** Compte de liaison avec l'Économat (finance-service ReversementResource). */
export interface CompteReversement {
  duParParoisse?: number;
  resteDuParParoisse?: number;
  obligationsEnRetard?: number;
  montantEnRetard?: number;
  plusAncienneEcheance?: string | null;
}

export interface SourcesAlertes {
  aujourdHui: Date;
  clotureCourante?: ClotureSemaine | null;
  clotureEcoulee?: ClotureSemaine | null;
  compte?: CompteReversement | null;
  intentions?: MassIntention[] | null;
  demandesSacrement?: SacrementRequest[] | null;
  prieresARelire?: number | null;
  visites?: VisitePastorale[] | null;
}

const RANG: Record<Urgence, number> = { jour: 0, semaine: 1, info: 2 };

export const fcfa = (v: number) => montant(v);

function jourIso(d: Date, decalage = 0): string {
  return toIsoDay(new Date(d.getFullYear(), d.getMonth(), d.getDate() + decalage));
}

function dateCourte(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
}

export function calculerAlertes(s: SourcesAlertes): Alerte[] {
  const out: Alerte[] = [];
  const today = jourIso(s.aujourdHui);
  const ilYaSeptJours = jourIso(s.aujourdHui, -7);

  // 1. Offrandes des messes saisies mais pas encore validées (clôture). Une
  // correction (contre-écriture) attend aussi le curé, mais se dit
  // « correction » (tournage du 21/10/2026, D1) : alerte à part.
  const ecoulee = enAttenteDuCure(
    s.clotureEcoulee?.totaux?.quetesEnAttente,
    s.clotureEcoulee?.totaux?.correctionsEnAttente,
  );
  if (ecoulee.offrandes > 0) {
    out.push({
      code: "dons-a-valider-semaine-ecoulee",
      urgence: "jour",
      cle: "donsAValiderEcoulee",
      params: { n: ecoulee.offrandes },
      // L'alerte ouvre la clôture sur la semaine dont elle parle.
      route: "/cloture?semaine=ecoulee",
      pageId: 9,
    });
  }
  if (ecoulee.corrections > 0) {
    out.push({
      code: "corrections-a-valider-semaine-ecoulee",
      urgence: "jour",
      cle: "correctionsAValiderEcoulee",
      params: { n: ecoulee.corrections },
      route: "/cloture?semaine=ecoulee",
      pageId: 9,
    });
  }
  const courante = enAttenteDuCure(
    s.clotureCourante?.totaux?.quetesEnAttente,
    s.clotureCourante?.totaux?.correctionsEnAttente,
  );
  if (courante.offrandes > 0) {
    out.push({
      code: "dons-a-valider-semaine",
      urgence: "semaine",
      cle: "donsAValider",
      params: { n: courante.offrandes },
      route: "/cloture?semaine=courante",
      pageId: 9,
    });
  }
  if (courante.corrections > 0) {
    out.push({
      code: "corrections-a-valider-semaine",
      urgence: "semaine",
      cle: "correctionsAValider",
      params: { n: courante.corrections },
      route: "/cloture?semaine=courante",
      pageId: 9,
    });
  }

  // 2. Versement à l'Économat : en retard (rouge) ou dû (orange / info).
  if (s.compte) {
    const retard = Number(s.compte.obligationsEnRetard ?? 0);
    const montantRetard = Number(s.compte.montantEnRetard ?? 0);
    const resteDu = Number(s.compte.resteDuParParoisse ?? s.compte.duParParoisse ?? 0);
    if (retard > 0 && montantRetard > 0) {
      out.push({
        code: "versement-en-retard",
        urgence: "jour",
        cle: "versementEnRetard",
        params: { montant: fcfa(montantRetard), n: retard },
        route: "/reversements",
        pageId: 90,
      });
    } else if (resteDu > 0) {
      const echeance = s.compte.plusAncienneEcheance?.slice(0, 10) ?? null;
      const proche = echeance != null && echeance <= jourIso(s.aujourdHui, 7);
      out.push({
        code: "versement-du",
        urgence: proche ? "semaine" : "info",
        cle: echeance ? "versementDuEcheance" : "versementDu",
        params: echeance ? { montant: fcfa(resteDu), date: dateCourte(echeance) } : { montant: fcfa(resteDu) },
        route: "/reversements",
        pageId: 90,
      });
    }
  }

  // 3. Intentions de messe passées sans célébration marquée.
  if (s.intentions) {
    const passees = s.intentions.filter((mi) => {
      const statut = String(mi.status ?? "").toUpperCase();
      const jour = String(mi.dateMesse ?? "").slice(0, 10);
      return (statut === "" || statut === "PLANIFIEE") && jour !== "" && jour < today;
    });
    const anciennes = passees.filter((mi) => String(mi.dateMesse).slice(0, 10) < ilYaSeptJours).length;
    const recentes = passees.length - anciennes;
    if (anciennes > 0) {
      out.push({
        code: "intentions-non-celebrees-anciennes",
        urgence: "jour",
        cle: "intentionsAnciennes",
        params: { n: anciennes },
        // Vue « célébration non marquée » de l'écran des intentions (D079).
        route: "/intentions?filtre=non-celebrees",
        pageId: 7,
      });
    }
    if (recentes > 0) {
      out.push({
        code: "intentions-non-celebrees",
        urgence: "semaine",
        cle: "intentionsRecentes",
        params: { n: recentes },
        // Vue « célébration non marquée » de l'écran des intentions (D079).
        route: "/intentions?filtre=non-celebrees",
        pageId: 7,
      });
    }
  }

  // 4. Intentions de prière à relire (fenêtre de 24 heures : à traiter dans la journée).
  const aRelire = Number(s.prieresARelire ?? 0);
  if (aRelire > 0) {
    out.push({
      code: "prieres-a-relire",
      urgence: "jour",
      cle: "prieresARelire",
      params: { n: aRelire },
      route: "/intentions",
      pageId: 7,
    });
  }

  // 5. Demandes de sacrement soumises, sans réponse.
  if (s.demandesSacrement) {
    const enAttente = s.demandesSacrement.filter((d) => String(d.status ?? "SUBMITTED").toUpperCase() === "SUBMITTED");
    const onctions = enAttente.filter((d) => d.sacramentType === "ANOINTING_SICK").length;
    const autres = enAttente.length - onctions;
    if (onctions > 0) {
      out.push({
        code: "onctions-en-attente",
        urgence: "jour",
        cle: "onctionsEnAttente",
        params: { n: onctions },
        route: "/preparation-sacrements",
        pageId: 42,
      });
    }
    if (autres > 0) {
      out.push({
        code: "sacrements-en-attente",
        urgence: "semaine",
        cle: "sacrementsEnAttente",
        params: { n: autres },
        route: "/preparation-sacrements",
        pageId: 42,
      });
    }
  }

  // 6. Visites de malades : hospitalisés (rouge), en retard ou à reprogrammer (orange).
  if (s.visites) {
    const aFaire = s.visites.filter((v) => v.statut === "PROGRAMMEE" || v.statut === "A_REPROGRAMMER");
    const hospitalises = aFaire.filter((v) => v.motif === "HOSPITALISE").length;
    const enRetard = aFaire.filter(
      (v) =>
        v.motif !== "HOSPITALISE" &&
        (v.statut === "A_REPROGRAMMER" || (v.dateVisitePrevue != null && v.dateVisitePrevue.slice(0, 10) < today)),
    ).length;
    if (hospitalises > 0) {
      out.push({
        code: "visites-hospitalises",
        urgence: "jour",
        cle: "visitesHospitalises",
        params: { n: hospitalises },
        route: "/visites-malades",
        pageId: 83,
      });
    }
    if (enRetard > 0) {
      out.push({
        code: "visites-en-retard",
        urgence: "semaine",
        cle: "visitesEnRetard",
        params: { n: enRetard },
        route: "/visites-malades",
        pageId: 83,
      });
    }
  }

  // Tri stable par urgence (l'ordre ci-dessus départage).
  return out
    .map((a, i) => ({ a, i }))
    .sort((x, y) => RANG[x.a.urgence] - RANG[y.a.urgence] || x.i - y.i)
    .map(({ a }) => a);
}
